import { useRef, useCallback, useEffect } from 'react';
import trackEvent from 'analytics/posthog/trackEvent';
import type { EventProperties } from 'analytics/posthog/types';

const useDebouncedTrack = (delay = 500) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingEventRef = useRef<{
        eventName: string;
        properties?: EventProperties;
    } | null>(null);

    const debounceTrack = useCallback(
        (eventName: string, properties?: EventProperties) => {
            pendingEventRef.current = { eventName, properties };

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                if (pendingEventRef.current) {
                    trackEvent(
                        pendingEventRef.current.eventName,
                        pendingEventRef.current.properties,
                    );
                    pendingEventRef.current = null;
                }
            }, delay);
        },
        [delay],
    );

    const cancelDebounce = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        pendingEventRef.current = null;
    }, []);

    const flushPending = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (pendingEventRef.current) {
            trackEvent(
                pendingEventRef.current.eventName,
                pendingEventRef.current.properties,
            );
            pendingEventRef.current = null;
        }
    }, []);

    useEffect(
        () => () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    return { debounceTrack, cancelDebounce, flushPending };
};

export default useDebouncedTrack;
